import { createRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { CircleCheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useModalBounds } from '@/hooks/useModalBounds';
import { cn } from '@/lib/utils';
import { Option } from '@/shared/types/rules';

type DropdownOption = Option & { disabled?: boolean };

type Props = {
  options: DropdownOption[];
  index?: number;
  handleOptionClick: (x: string) => void;
  searchBar?: boolean;
  selected: string;
  height?: 'large' | 'xlarge' | 'medium';
  lowerCase?: boolean;
  renderValue?: boolean;
  translationPrefix?: string;
  fullWidth?: boolean;
  alignment?: 'right' | 'left';
};

const heightOffset = { medium: 'top-10', large: 'top-10', xlarge: 'top-[50px]' };

export const ConditionOptionsModal = ({
  options,
  index = 0,
  handleOptionClick,
  searchBar = false,
  selected,
  height,
  lowerCase = false,
  renderValue = false,
  translationPrefix = '',
  fullWidth = false,
  alignment = 'left',
  ref,
}: Props & { ref?: React.Ref<HTMLDivElement> }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current, [innerRef]);
  useModalBounds(innerRef);

  const handleTyping = useCallback((e) => {
    setQuery(e.target.value.toLowerCase().trim());
  }, []);
  const optionRefs = useRef([]);
  optionRefs.current = options.map((_, i) => optionRefs.current[i] ?? createRef());

  const selectedIndex = options.findIndex((x) => x.label === selected);

  useEffect(() => {
    if (selectedIndex > -1) {
      optionRefs.current[selectedIndex].current?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleClick = useCallback(
    (x) => {
      handleOptionClick(x);
    },
    [handleOptionClick]
  );

  const generateText = useCallback(
    (option) => {
      if (renderValue) {
        return t(`${translationPrefix}${option.value}`);
      }
      return option.label;
    },
    [renderValue, t, translationPrefix]
  );

  const filteredOptions = options?.filter((option) =>
    generateText(option)?.toLowerCase()?.includes(query)
  );

  return (
    <div
      className={cn(
        'absolute top-7 left-px z-[4] mt-1 flex max-h-80 w-64 min-w-64 flex-col overflow-auto rounded border border-stroke bg-background p-2 text-sm leading-5 font-normal shadow-[var(--shadow-primary)]',
        searchBar && 'px-2 pt-0 pb-2',
        fullWidth && 'w-full min-w-0',
        alignment === 'right' && 'right-px left-auto',
        height && heightOffset[height]
      )}
      ref={innerRef}
    >
      {searchBar && (
        <div className="sticky top-0 bg-background pt-3">
          <input
            onChange={handleTyping}
            autoFocus
            placeholder={t('common.search_dots')}
            className="mb-2 h-[30px] w-full rounded-md border border-stroke bg-background pl-2 text-xs leading-4 outline-none"
          />
        </div>
      )}
      {filteredOptions.map((x, i) => {
        const isSelected = x.value === selected;
        const isDisabled = x.disabled;
        return (
          <span
            key={x.label}
            className={cn(
              'block h-8 min-h-8 cursor-pointer truncate px-2 leading-8 hover:rounded hover:bg-bg-muted',
              searchBar && 'px-2 pt-0 pb-2',
              lowerCase && 'lowercase',
              isDisabled && 'cursor-not-allowed text-bg-muted',
              isSelected &&
                'flex justify-between rounded bg-background text-sm leading-5 font-semibold text-informational'
            )}
            onClick={() => {
              if (!isDisabled) {
                handleClick({ value: x.value, label: x.label, index });
              }
            }}
            onKeyDown={() => {
              if (!isDisabled) {
                handleClick({ value: x.value, label: x.label, index });
              }
            }}
            role="button"
            tabIndex={0}
            ref={optionRefs.current[i]}
          >
            {generateText(x)}
            {isSelected && <CircleCheckIcon size={18} className="text-foreground-muted" />}
          </span>
        );
      })}
    </div>
  );
};
